import { useTranslation } from 'react-i18next';
import { AlertTriangle, CalendarX, PackageX, TrendingUp } from 'lucide-react';
import { KpiCard } from '@/components/shared/KpiCard';
import type { ForecastReviewDataValidationSummary } from '@/services/forecastReviewService';

export type DataValidationKpiFilter = 'all' | 'gaps' | 'stockout' | 'outlier';

interface DataValidationKpiCardsProps {
  summary: ForecastReviewDataValidationSummary | null;
  loading?: boolean;
  activeFilter: DataValidationKpiFilter;
  onFilterChange: (filter: DataValidationKpiFilter) => void;
}

function pctDisplay(ratio: number | null | undefined): number {
  if (ratio == null) return 0;
  return Math.round(ratio * 1000) / 10;
}

function activeRing(active: boolean): string {
  return active ? 'ring-2 ring-[#1681ba] ring-offset-1 rounded-lg' : '';
}

export function DataValidationKpiCards({
  summary,
  loading,
  activeFilter,
  onFilterChange,
}: DataValidationKpiCardsProps) {
  const { t } = useTranslation();

  const toggle = (filter: DataValidationKpiFilter) => {
    onFilterChange(activeFilter === filter ? 'all' : filter);
  };

  const scopeSubtitle = t('forecastModule.dataValidation.kpis.inScope', {
    count: summary?.products_in_scope ?? 0,
  });

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <div className={activeRing(activeFilter === 'all')}>
        <KpiCard
          title={t('forecastModule.dataValidation.kpis.productsWithIssues')}
          value={summary?.products_with_issues ?? '—'}
          isCount
          icon={<TrendingUp className="h-5 w-5 text-[#1681ba]" />}
          subtitle={t('forecastModule.dataValidation.kpis.productsWithIssuesHint', {
            pct: pctDisplay(summary?.products_with_issues_pct),
          })}
          loading={loading}
          onClick={() => onFilterChange('all')}
        />
      </div>
      <div className={activeRing(activeFilter === 'gaps')}>
        <KpiCard
          title={t('forecastModule.dataValidation.kpis.productsWithGaps')}
          value={pctDisplay(summary?.products_with_gaps_pct)}
          isPercentage
          icon={<CalendarX className="h-5 w-5 text-gray-500" />}
          subtitle={t('forecastModule.dataValidation.kpis.countOfScope', {
            count: summary?.products_with_gaps ?? 0,
            scope: scopeSubtitle,
          })}
          loading={loading}
          onClick={() => toggle('gaps')}
        />
      </div>
      <div className={activeRing(activeFilter === 'stockout')}>
        <KpiCard
          title={t('forecastModule.dataValidation.kpis.productsWithStockout')}
          value={pctDisplay(summary?.products_with_stockout_pct)}
          isPercentage
          icon={<PackageX className="h-5 w-5 text-red-500" />}
          variant={(summary?.products_with_stockout ?? 0) > 0 ? 'warning' : 'default'}
          subtitle={t('forecastModule.dataValidation.kpis.countOfScope', {
            count: summary?.products_with_stockout ?? 0,
            scope: scopeSubtitle,
          })}
          loading={loading}
          onClick={() => toggle('stockout')}
        />
      </div>
      <div className={activeRing(activeFilter === 'outlier')}>
        <KpiCard
          title={t('forecastModule.dataValidation.kpis.productsWithOutliers')}
          value={pctDisplay(summary?.products_with_outliers_pct)}
          isPercentage
          icon={<AlertTriangle className="h-5 w-5 text-orange-500" />}
          variant={(summary?.products_with_outliers ?? 0) > 0 ? 'warning' : 'default'}
          subtitle={t('forecastModule.dataValidation.kpis.countOfScope', {
            count: summary?.products_with_outliers ?? 0,
            scope: scopeSubtitle,
          })}
          loading={loading}
          onClick={() => toggle('outlier')}
        />
      </div>
    </div>
  );
}
